import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import { 
  FileText, 
  Mail, 
  Phone, 
  Facebook, 
  Instagram, 
  MessageCircle 
} from 'lucide-react';

const Footer: React.FC = () => {
  const { language } = useLocalization();
  const navigate = useNavigate();

  return (
    <footer className="bg-gray-900 dark:bg-gray-800 text-white py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">
                {language === 'ar' ? 'مرافعة' : 'Morafaa'}
              </h3>
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              {language === 'ar' 
                ? 'منصة شاملة لإدارة الممارسة القانونية مصممة خصيصاً للمحامين في مصر'
                : 'Comprehensive legal practice management platform designed specifically for lawyers in Egypt'
              }
            </p>
            <div className="flex space-x-4 rtl:space-x-reverse">
              <a href="mailto:morafaaapp@gmail.com" className="text-gray-400 hover:text-white transition-colors" title="Email">
                <Mail className="w-5 h-5" />
              </a>
              <a href="tel:+201202322241" className="text-gray-400 hover:text-white transition-colors" title="Phone">
                <Phone className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/morafaaapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/morafaaapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://wa.me/201202322241" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="WhatsApp">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {language === 'ar' ? 'روابط سريعة' : 'Quick Links'}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate('/')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {language === 'ar' ? 'الرئيسية' : 'Home'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/login')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/signup')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {language === 'ar' ? 'إنشاء حساب' : 'Sign Up'}
                </button>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {language === 'ar' ? 'الشروط والأحكام' : 'Legal'}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigate('/terms-and-conditions')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/privacy-policy')}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                </button>
              </li>
            </ul>
          </div>

          {/* Download Apps */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {language === 'ar' ? 'تحميل التطبيق' : 'Download App'}
            </h3>
            <div className="flex flex-col gap-3">
              <a 
                href="https://play.google.com/store/apps/details?id=com.optimizeware.morafaa" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                  alt="Get it on Google Play" 
                  className="h-12 hover:opacity-80 transition-opacity"
                />
              </a>
              <button 
                onClick={() => alert(language === 'ar' ? 'قريباً على متجر التطبيقات' : 'Coming soon on the App Store')}
                className="inline-block cursor-pointer"
              >
                <img 
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                  alt="Download on the App Store" 
                  className="h-12 hover:opacity-80 transition-opacity"
                />
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-gray-400">
            © 2025 {language === 'ar' ? 'مرافعة' : 'Morafaa'}. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
